import { useEffect, useRef, useState } from 'react';
import { Icon } from '../ui/Icon';
import { t } from '../../i18n/t';
import i18n from '../../i18n/config';
import { localeSwitchHref } from '../../i18n/path';

const LOCALES = [
  { code: 'lv', flag: '🇱🇻' },
  { code: 'en', flag: '🇬🇧' },
  { code: 'ru', flag: '🇷🇺' },
] as const;

function localeHref(locale: string): string {
  return localeSwitchHref(locale);
}

export function LocaleSwitcher({ compact = false }: { compact?: boolean }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const current = LOCALES.find((locale) => locale.code === i18n.locale) ?? LOCALES[0];

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-primary-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:ring-offset-2"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('ui.locale_switcher.label')}
        onClick={() => setOpen((value) => !value)}
      >
        <span aria-hidden="true">{current.flag}</span>
        {!compact && <span>{t(`ui.locales.${current.code}`)}</span>}
        <Icon
          name="chevron-down"
          className={`h-4 w-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
          strokeWidth={2}
        />
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute right-0 z-50 mt-2 min-w-[11.5rem] overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-lg"
        >
          {LOCALES.map((locale) => {
            const selected = locale.code === current.code;
            return (
              <li key={locale.code} role="option" aria-selected={selected}>
                <a
                  href={localeHref(locale.code)}
                  className={`flex items-center gap-2.5 px-3 py-2 text-sm transition ${
                    selected
                      ? 'bg-slate-50 font-semibold text-primary-600'
                      : 'text-slate-700 hover:bg-slate-50'
                  }`}
                  hrefLang={locale.code}
                  onClick={() => setOpen(false)}
                >
                  <span aria-hidden="true">{locale.flag}</span>
                  {t(`ui.locales.${locale.code}`)}
                </a>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
